import React, { useState, useEffect, useCallback } from 'react';
import { isSuperAdmin, getBranchId, fetchWithAuth } from '../utils/auth';
import { API_URL } from '../config';
import { listGrades } from './student-registration/studentRegistrationApi';

const STATUS_COLORS = {
  submitted: { background: '#fef9c3', color: '#854d0e' },
  approved: { background: '#dcfce7', color: '#166534' },
  not_approved: { background: '#fee2e2', color: '#991b1b' },
};

const STATUS_LABELS = { submitted: 'Submitted', approved: 'Approved', not_approved: 'Not Approved' };

// Same fixed set the public form's volunteer-section dropdown uses -- kept
// as the filter options here too, since it's the confirmed section scheme.
const SECTIONS = ['A1', 'A2', 'A3', 'A4', 'B1', 'B2', 'B3', 'B4'];

// A request counts toward the "moving out" projection once it's either
// been decided in the family's favor or is still awaiting a decision --
// only a not_approved request is excluded, since that student isn't going
// anywhere. Mirrors the "full pipeline" choice made for the Batch Impact tab.
const MOVING_STATUSES = ['submitted', 'approved'];

// Quotes a CSV field only when it needs it (comma/quote/newline), same
// approach as the Roster page's CSV export.
const csvEscape = (value) => {
  const str = String(value ?? '');
  return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

const batchForSection = (section) => {
  const first = (section || '').trim()[0]?.toUpperCase();
  if (first === 'A') return 'A Batch';
  if (first === 'B') return 'B Batch';
  return null;
};

// Rough capacity cue against the "8-10 kids per section" norm mentioned for
// this school -- not a hard rule, just a quick visual flag for sections
// running thin (e.g. preschool, or a section after a wave of transfers).
const capacityColor = (count) => {
  if (count >= 6) return { background: '#dcfce7', color: '#166534' };
  if (count >= 3) return { background: '#fef9c3', color: '#854d0e' };
  return { background: '#fee2e2', color: '#991b1b' };
};

// CSV export for a "Remaining" bucket's underlying students -- clicking any
// Remaining number on the Batch Impact tab downloads exactly this list.
const REMAINING_CSV_HEADER = ['Student ID', 'Name', 'Grade', 'Section', 'Teacher', 'Parent 1', 'Parent 2', 'Phone', 'Email'];
const studentToCsvRow = (s) => [
  s.student_id, `${s.first_name || ''} ${s.last_name || ''}`.trim(),
  s.grade_name || '', s.section || '', s.teacher_email || '',
  s.mother_name || '', s.father_name || '', s.phone || '', s.primary_email || '',
];
const downloadRemainingCsv = (students, label) => {
  if (!students.length) return;
  const rows = students.map(studentToCsvRow);
  const csv = [REMAINING_CSV_HEADER, ...rows].map(row => row.map(csvEscape).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `remaining_${label.replace(/[^a-z0-9]+/gi, '_')}_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Builds the grade -> section -> {current, moving, remaining} grid the
// Batch Impact tab renders. `roster` is every current registration for
// this branch/year (from /transfer/roster, one row per student); `requests`
// is every transfer request for this branch (unfiltered by status) --
// filtered here to MOVING_STATUSES and grouped by each student's own
// current grade/section (already looked up live server-side, same as the
// main Requests table). Each row keeps its actual `remainingStudents` list
// (current roster minus anyone moving, by student id) so the on-screen
// number and its CSV export can never drift apart -- both come from the
// same array. Purely a client-side projection: nothing here is written.
const buildImpactGrid = (roster, requests, gradesList) => {
  const gradeOrder = gradesList.map(g => String(g.id));
  const gradeName = Object.fromEntries(gradesList.map(g => [String(g.id), g.name]));

  const currentByCell = {};
  for (const s of roster) {
    if (s.grade_id == null) continue;
    const gid = String(s.grade_id);
    const sec = s.section || 'No section';
    currentByCell[gid] = currentByCell[gid] || {};
    currentByCell[gid][sec] = currentByCell[gid][sec] || [];
    currentByCell[gid][sec].push(s);
  }

  const movingCountByCell = {};
  const movingStudentIds = new Set();
  for (const r of requests) {
    if (!MOVING_STATUSES.includes(r.status) || r.grade_id == null) continue;
    const gid = String(r.grade_id);
    const sec = r.section || 'No section';
    movingCountByCell[gid] = movingCountByCell[gid] || {};
    movingCountByCell[gid][sec] = (movingCountByCell[gid][sec] || 0) + 1;
    movingStudentIds.add(r.student_id);
  }

  const gradeIds = Array.from(new Set([...Object.keys(currentByCell), ...Object.keys(movingCountByCell)]))
    .sort((a, b) => gradeOrder.indexOf(a) - gradeOrder.indexOf(b));

  const sortSections = (secs) => secs.sort((a, b) => {
    if (a === 'No section') return 1;
    if (b === 'No section') return -1;
    return a.localeCompare(b);
  });

  return gradeIds.map(gid => {
    const sections = sortSections(Array.from(new Set([...Object.keys(currentByCell[gid] || {}), ...Object.keys(movingCountByCell[gid] || {})])));
    const rows = sections.map(sec => {
      const currentStudents = (currentByCell[gid] || {})[sec] || [];
      const moving = (movingCountByCell[gid] || {})[sec] || 0;
      const remainingStudents = currentStudents.filter(s => !movingStudentIds.has(s.student_id));
      return {
        section: sec, batch: batchForSection(sec),
        current: currentStudents.length, moving, remaining: remainingStudents.length,
        remainingStudents,
      };
    });
    return {
      gradeId: gid,
      gradeName: gradeName[gid] || `Grade #${gid}`,
      rows,
      remainingStudents: rows.flatMap(r => r.remainingStudents),
      totals: {
        current: rows.reduce((s, r) => s + r.current, 0),
        moving: rows.reduce((s, r) => s + r.moving, 0),
        remaining: rows.reduce((s, r) => s + r.remaining, 0),
      },
    };
  });
};

const AdminTransferRequests = () => {
  const superAdmin = isSuperAdmin();
  const [branches, setBranches] = useState([]);
  // For branch_admin this stays fixed at their own branch (no picker shown);
  // for super_admin it starts empty (they have no fixed branch) until picked.
  const [branchId, setBranchId] = useState(getBranchId() || '');

  const [settings, setSettings] = useState(null); // { enabled, access_code, intro_text, review_text, match_found_text, success_text, already_submitted_text }
  // Independently-editable content slots -- shown at each distinct moment
  // in the public flow. Each falls back to a sensible generic default on
  // the public side when left blank here.
  const [introDraft, setIntroDraft] = useState('');
  const [reviewDraft, setReviewDraft] = useState('');
  const [matchFoundDraft, setMatchFoundDraft] = useState('');
  const [successDraft, setSuccessDraft] = useState('');
  const [alreadySubmittedDraft, setAlreadySubmittedDraft] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [settingsMessage, setSettingsMessage] = useState('');

  const [grades, setGrades] = useState([]);
  const [gradeFilter, setGradeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sectionFilter, setSectionFilter] = useState('');
  const [batchFilter, setBatchFilter] = useState('');

  const [requests, setRequests] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // Which approved-and-not-yet-emailed rows are checked for the bulk
  // "Send Approval Email(s)" action -- deliberately a separate step from
  // Approve itself, so a batch of decisions can be reviewed before any
  // parent actually gets an email, and a failed send never blocks/undoes
  // the approval it's about.
  const [selectedForEmail, setSelectedForEmail] = useState(new Set());
  const [sendingEmail, setSendingEmail] = useState(false);

  // 'requests' (existing table, untouched) or 'impact' (new Batch Impact
  // projection) -- a second tab on this same page, per explicit request.
  const [activeTab, setActiveTab] = useState('requests');
  const [impactRoster, setImpactRoster] = useState([]);
  const [impactRequests, setImpactRequests] = useState([]);
  const [impactLoading, setImpactLoading] = useState(false);
  const [impactError, setImpactError] = useState('');

  useEffect(() => {
    if (superAdmin) {
      fetchWithAuth(`${API_URL}/api/admin/branches`)
        .then(r => r.json())
        .then(d => setBranches(d.branches || []))
        .catch(() => {});
    }
  }, [superAdmin]);

  useEffect(() => {
    listGrades(true).then(({ ok, data }) => { if (ok) setGrades(data.grades || []); });
  }, []);

  const loadSettings = useCallback(async () => {
    if (!branchId) { setSettings(null); return; }
    try {
      const res = await fetchWithAuth(`${API_URL}/api/admin/branches/${branchId}/transfer-settings`);
      const data = await res.json();
      if (res.ok && data.success) {
        setSettings(data.settings);
        setIntroDraft(data.settings.intro_text || '');
        setReviewDraft(data.settings.review_text || '');
        setMatchFoundDraft(data.settings.match_found_text || '');
        setSuccessDraft(data.settings.success_text || '');
        setAlreadySubmittedDraft(data.settings.already_submitted_text || '');
      }
    } catch { /* ignore -- settings section just stays empty */ }
  }, [branchId]);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    setError('');
    const params = new URLSearchParams();
    if (branchId) params.set('branch_id', branchId);
    if (gradeFilter) params.set('grade_id', gradeFilter);
    if (statusFilter) params.set('status', statusFilter);
    if (sectionFilter) params.set('section', sectionFilter);
    if (batchFilter) params.set('batch', batchFilter);
    try {
      const res = await fetchWithAuth(`${API_URL}/api/admin/transfer/requests?${params.toString()}`);
      const data = await res.json();
      if (res.ok) {
        setRequests(data.requests || []);
        setTotalCount(data.total_count ?? (data.requests || []).length);
      } else {
        setError(data.error || 'Failed to load transfer requests.');
      }
    } catch {
      setError('Network error.');
    }
    setLoading(false);
  }, [branchId, gradeFilter, statusFilter, sectionFilter, batchFilter]);

  useEffect(() => { loadRequests(); }, [loadRequests]);

  // Independent of the Requests table's own filters -- the impact
  // projection always needs the FULL roster/requests for this branch,
  // filtering to MOVING_STATUSES itself in buildImpactGrid.
  const loadImpactData = useCallback(async () => {
    if (!branchId) return;
    setImpactLoading(true);
    setImpactError('');
    try {
      const [rosterRes, reqRes] = await Promise.all([
        fetchWithAuth(`${API_URL}/api/admin/transfer/roster?branch_id=${branchId}`),
        fetchWithAuth(`${API_URL}/api/admin/transfer/requests?branch_id=${branchId}`),
      ]);
      const rosterData = await rosterRes.json();
      const reqData = await reqRes.json();
      if (rosterRes.ok && reqRes.ok) {
        setImpactRoster(rosterData.roster || []);
        setImpactRequests(reqData.requests || []);
      } else {
        setImpactError(rosterData.error || reqData.error || 'Failed to load batch impact data.');
      }
    } catch {
      setImpactError('Network error.');
    }
    setImpactLoading(false);
  }, [branchId]);

  useEffect(() => { if (activeTab === 'impact') loadImpactData(); }, [activeTab, loadImpactData]);

  const handleToggleEnabled = async () => {
    if (!branchId || !settings) return;
    setSavingSettings(true);
    setSettingsMessage('');
    try {
      const res = await fetchWithAuth(`${API_URL}/api/admin/branches/${branchId}/transfer-settings`, {
        method: 'PUT', body: JSON.stringify({ transfer_form_enabled: !settings.enabled }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSettings(data.settings);
        setSettingsMessage(data.settings.enabled ? 'Transfer form enabled.' : 'Transfer form disabled.');
      } else {
        setSettingsMessage(data.error || 'Failed to update.');
      }
    } catch {
      setSettingsMessage('Network error.');
    }
    setSavingSettings(false);
  };

  const handleSaveText = async () => {
    if (!branchId) return;
    setSavingSettings(true);
    setSettingsMessage('');
    try {
      const res = await fetchWithAuth(`${API_URL}/api/admin/branches/${branchId}/transfer-settings`, {
        method: 'PUT',
        body: JSON.stringify({
          intro_text: introDraft, review_text: reviewDraft, match_found_text: matchFoundDraft,
          success_text: successDraft, already_submitted_text: alreadySubmittedDraft,
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSettings(data.settings);
        setSettingsMessage('Saved.');
      } else {
        setSettingsMessage(data.error || 'Failed to save.');
      }
    } catch {
      setSettingsMessage('Network error.');
    }
    setSavingSettings(false);
  };

  const handleRegenerate = async () => {
    if (!branchId) return;
    if (!window.confirm('Regenerate the access code? The old code will stop working immediately.')) return;
    setRegenerating(true);
    setSettingsMessage('');
    try {
      const res = await fetchWithAuth(`${API_URL}/api/admin/branches/${branchId}/transfer-code/regenerate`, { method: 'POST' });
      const data = await res.json();
      if (res.ok && data.success) {
        setSettings(s => ({ ...s, access_code: data.access_code }));
        setSettingsMessage('Code regenerated.');
      } else {
        setSettingsMessage(data.error || 'Failed to regenerate.');
      }
    } catch {
      setSettingsMessage('Network error.');
    }
    setRegenerating(false);
  };

  const handleCopyCode = () => {
    if (settings?.access_code) {
      navigator.clipboard?.writeText(settings.access_code).catch(() => {});
      setSettingsMessage('Code copied.');
    }
  };

  const handleDecision = async (id, status) => {
    const verb = status === 'approved' ? 'approve' : 'mark as not approved';
    if (!window.confirm(`Are you sure you want to ${verb} this transfer request?`)) return;
    setError('');
    setMessage('');
    try {
      const res = await fetchWithAuth(`${API_URL}/api/admin/transfer/requests/${id}/decision`, {
        method: 'PUT', body: JSON.stringify({ status }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setMessage('Updated.');
        loadRequests();
      } else {
        setError(data.error || 'Failed to update.');
      }
    } catch {
      setError('Network error.');
    }
  };

  const handleDelete = async (id, studentName) => {
    if (!window.confirm(`Permanently delete the transfer request for ${studentName}? This cannot be undone.`)) return;
    setError('');
    setMessage('');
    try {
      const res = await fetchWithAuth(`${API_URL}/api/admin/transfer/requests/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (res.ok && data.success) {
        setMessage('Deleted.');
        loadRequests();
      } else {
        setError(data.error || 'Failed to delete.');
      }
    } catch {
      setError('Network error.');
    }
  };

  const toggleSelectForEmail = (id) => {
    setSelectedForEmail(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleSendApprovalEmails = async () => {
    const ids = Array.from(selectedForEmail);
    if (ids.length === 0) return;
    if (!window.confirm(`Send the approval email to ${ids.length} ${ids.length === 1 ? 'family' : 'families'}?`)) return;
    setSendingEmail(true);
    setError('');
    setMessage('');
    try {
      const res = await fetchWithAuth(`${API_URL}/api/admin/transfer/requests/send-approval-email`, {
        method: 'POST', body: JSON.stringify({ request_ids: ids }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        const failed = (data.results || []).filter(r => !r.success);
        setMessage(
          failed.length === 0
            ? `Sent ${ids.length} email(s).`
            : `Sent ${ids.length - failed.length} of ${ids.length}. Failed: ` +
              failed.map(f => `#${f.request_id} (${f.error})`).join(', ')
        );
        setSelectedForEmail(new Set());
        loadRequests();
      } else {
        setError(data.error || 'Failed to send emails.');
      }
    } catch {
      setError('Network error.');
    }
    setSendingEmail(false);
  };

  const handleDownloadCsv = () => {
    const header = [
      'Student ID', 'First Name', 'Last Name', 'From Branch', 'To Branch', 'Grade', 'Section', 'Batch', 'Status', 'Email Sent', 'Submitted', 'Decided',
      'Volunteering as Teacher', 'Volunteer Name', 'Volunteer Grade', 'Volunteer Section',
    ];
    const rows = requests.map(r => [
      r.student?.id, r.student?.first_name, r.student?.last_name,
      r.from_branch_name || '', r.to_branch_name || '', r.grade_name || '', r.section || '', r.batch || '',
      STATUS_LABELS[r.status] || r.status,
      r.email_sent_at ? new Date(r.email_sent_at).toLocaleString() : '',
      r.created_at ? new Date(r.created_at).toLocaleString() : '',
      r.decided_at ? new Date(r.decided_at).toLocaleString() : '',
      r.volunteer_teacher ? 'Yes' : 'No',
      r.volunteer_teacher_name || '',
      r.volunteer_grade_name || '',
      r.volunteer_section || '',
    ]);
    const csv = [header, ...rows].map(row => row.map(csvEscape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `transfer_requests_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1100px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0 }}>Transfer Requests</h2>
        {superAdmin && branches.length > 0 && (
          <select value={branchId} onChange={e => setBranchId(e.target.value)}>
            <option value="">Select a branch…</option>
            {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        )}
      </div>

      {error && <div className="error-banner">{error}</div>}
      {message && <p style={{ color: '#166534', fontSize: '13px' }}>{message}</p>}

      {!branchId ? (
        <p style={{ color: '#94a3b8' }}>Select a branch to manage its transfer form.</p>
      ) : (
        <>
          <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: '1px solid #e2e8f0' }}>
            <button
              className={activeTab === 'requests' ? 'btn-primary' : 'btn-secondary'}
              style={{ borderRadius: '6px 6px 0 0' }}
              onClick={() => setActiveTab('requests')}
            >
              Requests
            </button>
            <button
              className={activeTab === 'impact' ? 'btn-primary' : 'btn-secondary'}
              style={{ borderRadius: '6px 6px 0 0' }}
              onClick={() => setActiveTab('impact')}
            >
              Batch Impact
            </button>
          </div>

          {activeTab === 'requests' && (
          <>
          <div className="form-card" style={{ marginBottom: '24px' }}>
            <h3>Public Form Settings</h3>
            {settingsMessage && <p style={{ fontSize: '13px', color: '#166534' }}>{settingsMessage}</p>}
            <div className="form-row">
              <label>
                <input type="checkbox" checked={!!settings?.enabled} onChange={handleToggleEnabled} disabled={savingSettings} />
                {' '}Transfer form enabled
              </label>
            </div>
            <div className="form-row">
              <label>Access Code</label>
              {settings?.access_code ? (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <code style={{ fontSize: '16px', fontWeight: 600 }}>{settings.access_code}</code>
                  <button type="button" className="btn-sm" onClick={handleCopyCode}>Copy</button>
                  <button type="button" className="btn-sm" onClick={handleRegenerate} disabled={regenerating}>
                    {regenerating ? 'Regenerating…' : 'Regenerate Code'}
                  </button>
                </div>
              ) : (
                <span style={{ color: '#94a3b8' }}>Enable the form to generate a code.</span>
              )}
            </div>
            <div className="form-row">
              <label>Intro text (shown first, before the name/DOB/phone lookup)</label>
              <textarea
                value={introDraft}
                onChange={e => setIntroDraft(e.target.value)}
                rows={3}
                style={{ width: '100%', boxSizing: 'border-box' }}
                placeholder="e.g. Welcome! Please have your child's date of birth and phone number ready…"
              />
            </div>
            <div className="form-row">
              <label>Review text (shown with the matched student(s), before submitting)</label>
              <textarea
                value={reviewDraft}
                onChange={e => setReviewDraft(e.target.value)}
                rows={3}
                style={{ width: '100%', boxSizing: 'border-box' }}
                placeholder="e.g. Please review the following before submitting your request…"
              />
            </div>
            <div className="form-row">
              <label>Match found text — replaces the "we found N student(s)..." message entirely if set</label>
              <textarea
                value={matchFoundDraft}
                onChange={e => setMatchFoundDraft(e.target.value)}
                rows={2}
                style={{ width: '100%', boxSizing: 'border-box' }}
                placeholder="Leave blank for the default (mentions how many students were found). If set, this fully replaces it."
              />
            </div>
            <div className="form-row">
              <label>Success text — replaces the "submitted to [branch]" confirmation entirely if set</label>
              <textarea
                value={successDraft}
                onChange={e => setSuccessDraft(e.target.value)}
                rows={3}
                style={{ width: '100%', boxSizing: 'border-box' }}
                placeholder="Leave blank for the default (mentions the branch name). If set, this fully replaces it."
              />
            </div>
            <div className="form-row">
              <label>Already-submitted text — replaces that whole message entirely if set (shown when a family resubmits a request already pending/approved)</label>
              <textarea
                value={alreadySubmittedDraft}
                onChange={e => setAlreadySubmittedDraft(e.target.value)}
                rows={2}
                style={{ width: '100%', boxSizing: 'border-box' }}
                placeholder="Leave blank for the default (mentions the branch name). If set, this fully replaces it."
              />
            </div>
            <button className="btn-secondary" onClick={handleSaveText} disabled={savingSettings}>
              {savingSettings ? 'Saving…' : 'Save Text'}
            </button>
          </div>

          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
            <select value={gradeFilter} onChange={e => setGradeFilter(e.target.value)}>
              <option value="">All grades</option>
              {grades.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="">All statuses</option>
              <option value="submitted">Submitted</option>
              <option value="approved">Approved</option>
              <option value="not_approved">Not Approved</option>
            </select>
            <select value={sectionFilter} onChange={e => setSectionFilter(e.target.value)}>
              <option value="">All sections</option>
              {SECTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={batchFilter} onChange={e => setBatchFilter(e.target.value)}>
              <option value="">All batches</option>
              <option value="A">A Batch</option>
              <option value="B">B Batch</option>
            </select>
            <button className="btn-secondary" onClick={handleDownloadCsv} disabled={requests.length === 0}>
              Download CSV
            </button>
            <button className="btn-primary" onClick={handleSendApprovalEmails} disabled={selectedForEmail.size === 0 || sendingEmail}>
              {sendingEmail ? 'Sending…' : `Send Approval Email${selectedForEmail.size !== 1 ? 's' : ''}${selectedForEmail.size ? ` (${selectedForEmail.size})` : ''}`}
            </button>
          </div>

          {!loading && (
            <p style={{ fontSize: '13px', color: '#475569', marginBottom: '12px' }}>
              <strong>{totalCount}</strong> {totalCount === 1 ? 'request' : 'requests'}
              {(gradeFilter || statusFilter || sectionFilter || batchFilter) ? ' matching filters' : ''}
            </p>
          )}

          {loading ? <p>Loading…</p> : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th></th><th>Student</th><th>From</th><th>To</th><th>Grade</th><th>Section</th><th>Batch</th><th>Volunteering as Teacher</th><th>Status</th><th>Email Sent</th><th>Submitted</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {requests.length === 0 ? (
                  <tr><td colSpan={12} style={{ textAlign: 'center', color: '#94a3b8', padding: '24px' }}>No transfer requests.</td></tr>
                ) : (
                  requests.map(r => (
                    <tr key={r.id}>
                      <td>
                        {r.status === 'approved' && (
                          <input
                            type="checkbox"
                            checked={selectedForEmail.has(r.id)}
                            onChange={() => toggleSelectForEmail(r.id)}
                            title="Select for approval email"
                          />
                        )}
                      </td>
                      <td>{r.student?.first_name} {r.student?.last_name} <span style={{ color: '#94a3b8' }}>({r.student?.id})</span></td>
                      <td>{r.from_branch_name || '—'}</td>
                      <td>{r.to_branch_name || '—'}</td>
                      <td>{r.grade_name || '—'}</td>
                      <td>{r.section || '—'}</td>
                      <td>{r.batch || '—'}</td>
                      <td>
                        {r.volunteer_teacher ? (
                          <span title={`${r.volunteer_teacher_name || ''} — Grade ${r.volunteer_grade_name || '—'}, Section ${r.volunteer_section || '—'}`}>
                            Yes — {r.volunteer_teacher_name || '—'} ({r.volunteer_grade_name || '—'} / {r.volunteer_section || '—'})
                          </span>
                        ) : 'No'}
                      </td>
                      <td>
                        <span style={{ ...STATUS_COLORS[r.status], padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 700 }}>
                          {STATUS_LABELS[r.status] || r.status}
                        </span>
                      </td>
                      <td style={{ fontSize: '12px', color: r.email_sent_at ? '#166534' : '#94a3b8' }}>
                        {r.email_sent_at ? new Date(r.email_sent_at).toLocaleString() : '—'}
                      </td>
                      <td>{r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</td>
                      <td>
                        {r.status === 'submitted' ? (
                          <>
                            <button className="btn-sm" onClick={() => handleDecision(r.id, 'approved')}>Approve</button>
                            {' '}
                            <button className="btn-sm btn-danger" onClick={() => handleDecision(r.id, 'not_approved')}>Not Approved</button>
                            {' '}
                          </>
                        ) : (
                          <span style={{ color: '#94a3b8', fontSize: '12px' }}>
                            {r.decided_at ? `Decided ${new Date(r.decided_at).toLocaleDateString()}` : '—'}
                            {' '}
                          </span>
                        )}
                        <button
                          className="btn-sm btn-danger"
                          onClick={() => handleDelete(r.id, `${r.student?.first_name || ''} ${r.student?.last_name || ''}`.trim() || r.student?.id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
          </>
          )}

          {activeTab === 'impact' && (
            <BatchImpact
              loading={impactLoading}
              error={impactError}
              grid={buildImpactGrid(impactRoster, impactRequests, grades)}
            />
          )}
        </>
      )}
    </div>
  );
};

// The Batch Impact tab: current roster vs. projected outflow (submitted +
// approved transfer requests), by grade and section, so an admin can see
// at a glance which sections thin out once pending/approved transfers are
// accounted for. Purely a read-only projection -- nothing here writes
// anything, and it never touches the Requests tab's own state/filters.
const BatchImpact = ({ loading, error, grid }) => {
  if (loading) return <p>Loading…</p>;
  if (error) return <div className="error-banner">{error}</div>;
  if (grid.length === 0) {
    return <p style={{ color: '#94a3b8' }}>No current registrations or transfer requests for this branch yet.</p>;
  }

  const grandTotal = grid.reduce((acc, g) => ({
    current: acc.current + g.totals.current,
    moving: acc.moving + g.totals.moving,
    remaining: acc.remaining + g.totals.remaining,
  }), { current: 0, moving: 0, remaining: 0 });
  const grandRemainingStudents = grid.flatMap(g => g.remainingStudents);

  // "Remaining here" broken down by batch -- rows with no A/B-prefixed
  // section (e.g. an ungraded "Evaluation" bucket) land in "No Batch",
  // shown only if it's actually non-zero so it doesn't clutter branches
  // where every section has a batch. Keeps the actual student list per
  // batch too, so each card's number can export exactly what it shows.
  const remainingByBatch = grid.reduce((acc, g) => {
    g.rows.forEach(row => {
      const key = row.batch === 'A Batch' ? 'a' : row.batch === 'B Batch' ? 'b' : 'other';
      acc[key].push(...row.remainingStudents);
    });
    return acc;
  }, { a: [], b: [], other: [] });

  return (
    <>
      <p style={{ fontSize: '13px', color: '#475569', marginBottom: '16px' }}>
        Projected impact of transfer requests that are <strong>Submitted</strong> or <strong>Approved</strong> (a
        Not Approved request doesn't move anyone). "Moving" assumes a student keeps the same section label at the
        new location. This is a read-only projection — it does not change any registration. Click any{' '}
        <strong>Remaining</strong> number to download that list of students.
      </p>

      <div style={{ display: 'flex', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div className="form-card" style={{ padding: '12px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: '22px', fontWeight: 700 }}>{grandTotal.current}</div>
          <div style={{ fontSize: '12px', color: '#64748b' }}>Current roster</div>
        </div>
        <div className="form-card" style={{ padding: '12px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: '22px', fontWeight: 700, color: '#b45309' }}>{grandTotal.moving}</div>
          <div style={{ fontSize: '12px', color: '#64748b' }}>Moving out</div>
        </div>
        <div className="form-card" style={{ padding: '12px 20px', textAlign: 'center' }}>
          <RemainingNumber students={grandRemainingStudents} label="Remaining here" textStyle={{ fontSize: '22px', fontWeight: 700, color: '#166534' }} />
          <div style={{ fontSize: '12px', color: '#64748b' }}>Remaining here</div>
        </div>
        <div className="form-card" style={{ padding: '12px 20px', textAlign: 'center' }}>
          <RemainingNumber students={remainingByBatch.a} label="Remaining — A Batch" textStyle={{ fontSize: '22px', fontWeight: 700, color: '#166534' }} />
          <div style={{ fontSize: '12px', color: '#64748b' }}>Remaining — A Batch</div>
        </div>
        <div className="form-card" style={{ padding: '12px 20px', textAlign: 'center' }}>
          <RemainingNumber students={remainingByBatch.b} label="Remaining — B Batch" textStyle={{ fontSize: '22px', fontWeight: 700, color: '#166534' }} />
          <div style={{ fontSize: '12px', color: '#64748b' }}>Remaining — B Batch</div>
        </div>
        {remainingByBatch.other.length > 0 && (
          <div className="form-card" style={{ padding: '12px 20px', textAlign: 'center' }}>
            <RemainingNumber students={remainingByBatch.other} label="Remaining — No Batch" textStyle={{ fontSize: '22px', fontWeight: 700, color: '#166534' }} />
            <div style={{ fontSize: '12px', color: '#64748b' }}>Remaining — No Batch</div>
          </div>
        )}
      </div>

      <table className="admin-table">
        <thead>
          <tr>
            <th>Grade / Section</th>
            <th>Batch</th>
            <th>Current</th>
            <th>Moving Out</th>
            <th>Remaining</th>
          </tr>
        </thead>
        <tbody>
          {grid.map(g => (
            <React.Fragment key={g.gradeId}>
              <tr style={{ background: '#f8fafc' }}>
                <td colSpan={2} style={{ fontWeight: 700 }}>{g.gradeName}</td>
                <td style={{ fontWeight: 700 }}>{g.totals.current}</td>
                <td style={{ fontWeight: 700, color: '#b45309' }}>{g.totals.moving}</td>
                <td style={{ fontWeight: 700 }}>
                  <RemainingNumber
                    students={g.remainingStudents} label={g.gradeName}
                    pillStyle={{ ...capacityColor(g.totals.remaining), padding: '2px 8px', borderRadius: '10px', fontSize: '12px' }}
                  />
                </td>
              </tr>
              {g.rows.map(row => (
                <tr key={`${g.gradeId}-${row.section}`}>
                  <td style={{ paddingLeft: '24px', color: '#475569' }}>{row.section}</td>
                  <td style={{ color: '#94a3b8' }}>{row.batch || '—'}</td>
                  <td>{row.current}</td>
                  <td style={{ color: row.moving > 0 ? '#b45309' : '#94a3b8' }}>{row.moving || '—'}</td>
                  <td>
                    <RemainingNumber
                      students={row.remainingStudents} label={`${g.gradeName}_${row.section}`}
                      pillStyle={{ ...capacityColor(row.remaining), padding: '2px 8px', borderRadius: '10px', fontSize: '12px' }}
                    />
                  </td>
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </>
  );
};

// A Remaining count rendered as a click-to-download control -- clicking it
// exports exactly the students summed into that number (never a re-query,
// so the download always matches what's on screen). Disabled at zero,
// since there's nothing to export. `pillStyle` renders it as the existing
// colored badge (table cells); `textStyle` renders it as plain bold text
// (summary cards) -- exactly one of the two is passed.
const RemainingNumber = ({ students, label, pillStyle, textStyle }) => {
  const count = students.length;
  const baseStyle = pillStyle
    ? { ...pillStyle, border: 'none', cursor: count ? 'pointer' : 'default' }
    : { ...textStyle, background: 'none', border: 'none', padding: 0, cursor: count ? 'pointer' : 'default' };
  return (
    <button
      type="button"
      onClick={() => downloadRemainingCsv(students, label)}
      disabled={count === 0}
      title={count ? `Download these ${count} student(s) as CSV` : 'No students remaining'}
      style={{ ...baseStyle, textDecoration: count ? 'underline' : 'none', textUnderlineOffset: '2px' }}
    >
      {count}
    </button>
  );
};

export default AdminTransferRequests;
